import { useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Textarea } from '../../../components/Input';
import { errorMessage } from '../../../lib/api-client';
import { formatRelative } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { TASKS_KEY, communicationApi, useHmQuestions } from '../api/communication-api';
import styles from '../Communication.module.css';

/**
 * O15 / Q10 — "comment / ask HR on any item (threads to the recruiter's E13 tasks)" (E16). The
 * question lands on the opening's recruiter's task list for the next morning; their answer comes
 * back here and as a notification. Built for a phone, like every HM screen (06-qol §10).
 */
export function AskHrSection({ requisitionId, sectionClass, titleClass }: { readonly requisitionId: string; readonly sectionClass: string; readonly titleClass: string }): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const questions = useHmQuestions([requisitionId]);
  const [question, setQuestion] = useState('');
  const [isSending, setIsSending] = useState(false);

  const ask = async (): Promise<void> => {
    if (question.trim().length < 5) return;
    setIsSending(true);
    try {
      await communicationApi.askHr({ requisitionId, question: question.trim() });
      await queryClient.invalidateQueries({ queryKey: TASKS_KEY });
      setQuestion('');
      show({ tone: 'success', message: 'Question sent to HR', detail: 'You will be told when they answer.' });
    } catch (cause) {
      show({ tone: 'danger', message: "The question wasn't sent", detail: errorMessage(cause) });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className={sectionClass}>
      <h3 className={titleClass}>Ask HR</h3>
      <form
        className={styles.editor}
        onSubmit={(event) => {
          event.preventDefault();
          void ask();
        }}
      >
        <Textarea aria-label="Your question" value={question} rows={3} maxLength={2000} placeholder="Can we see two more CVs this week?" onChange={(event) => setQuestion(event.target.value)} />
        <div className={styles.formActions}>
          <Button variant="outline" size="sm" type="submit" icon={Send} isPending={isSending} disabled={question.trim().length < 5}>
            Ask
          </Button>
        </div>
      </form>
      {(questions.data ?? []).map((row) => (
        <div key={row.id} className={styles.itemBody}>
          <p className={styles.question}>{row.description}</p>
          <span className={styles.meta}>
            Asked {formatRelative(row.createdAt)} · {row.answer ? `${row.assignee?.fullName ?? 'HR'} answered` : `with ${row.assignee?.fullName ?? 'HR'}`}
          </span>
          {row.answer && <p className={styles.itemText}>{row.answer}</p>}
        </div>
      ))}
    </section>
  );
}
